/**
 * Util canónico del documento de identidad (users.tipoDocumento + users.Documento).
 * La normalización y validación dependen del tipo: CI/DNI/RUT son numéricos;
 * Pasaporte/Otro son alfanuméricos (un pasaporte "ab-123456" se guarda "AB123456").
 * Reemplaza a los `getDocumentValidationMessage`/`normalizeDocumentNumber`
 * duplicados por pantalla y al uso de normalize.js#normalizeDocument (solo
 * dígitos), que corrompía pasaportes.
 */

export const DOC_TYPES = ["CI", "DNI", "Pasaporte", "RUT", "Otro"];

export const isNumericDocType = (tipo) => ["CI", "DNI", "RUT"].includes(String(tipo || "CI").trim());

/** Normaliza según tipo: numéricos → solo dígitos; Pasaporte/Otro → A-Z0-9 upper. */
export function normalizeDocumento(value, tipo = "CI") {
  const s = String(value || "").trim();
  if (isNumericDocType(tipo)) return s.replace(/[^\d]/g, "");
  return s.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Variantes del documento para queries de dedupe/activación (equality sobre
 * users.Documento). Incluye siempre la variante solo-dígitos porque hay
 * pasaportes legacy guardados corrompidos a dígitos, y la alfanumérica por si
 * el tipo elegido no coincide con cómo se guardó. Se filtran variantes cortas
 * (<6) para no matchear basura.
 */
export function documentoQueryVariants(value, tipo = "CI") {
  const raw = String(value || "").trim();
  const candidates = [
    raw,
    normalizeDocumento(raw, tipo),
    raw.replace(/[^\d]/g, ""),
    raw.toUpperCase().replace(/[^A-Z0-9]/g, ""),
  ];
  return [...new Set(candidates.filter((v) => v && v.length >= 6))];
}

// Rangos de largo (sobre el valor normalizado) por tipo de documento.
const DOC_LIMITS = {
  CI: [6, 8],
  DNI: [6, 12],
  Pasaporte: [6, 12],
  RUT: [12, 12],
  Otro: [6, 20],
};

/**
 * Valida formato según tipo. Devuelve "" si está ok, o el mensaje de error.
 * Para tipos numéricos rechaza letras; Pasaporte/Otro aceptan alfanumérico.
 */
export function getDocumentoValidationMessage(tipo, value) {
  const t = DOC_LIMITS[tipo] ? tipo : "CI";
  const raw = String(value || "").trim();
  if (!raw) return "Ingresá el documento.";
  if (isNumericDocType(t) && /[a-zA-Z]/.test(raw)) {
    return `El documento tipo ${t} solo lleva números. Si tiene letras, elegí Pasaporte u Otro.`;
  }
  const norm = normalizeDocumento(raw, t);
  if (!norm) return "Ingresá el documento.";
  const [min, max] = DOC_LIMITS[t];
  if (norm.length < min || norm.length > max) {
    const rango = min === max ? `${min}` : `entre ${min} y ${max}`;
    const unidad = isNumericDocType(t) ? "dígitos" : "caracteres";
    return `El documento tipo ${t} debe tener ${rango} ${unidad}.`;
  }
  return "";
}

/**
 * Dígito verificador de la cédula uruguaya. `ci` = número completo con
 * verificador (7 u 8 dígitos: 6-7 del número + 1 verificador). Algoritmo
 * estándar: número sin verificador con pad de ceros a 7, coeficientes
 * [2,9,8,7,6,3,4], verificador = (10 - suma % 10) % 10.
 */
export function validateCIUY(ci) {
  const digits = String(ci || "").replace(/[^\d]/g, "");
  if (digits.length < 7 || digits.length > 8) {
    return { valid: false, message: "La cédula debe tener 7 u 8 dígitos (incluido el verificador)." };
  }
  const body = digits.slice(0, -1).padStart(7, "0");
  const check = Number(digits.slice(-1));
  const coefs = [2, 9, 8, 7, 6, 3, 4];
  const sum = body.split("").reduce((acc, d, i) => acc + Number(d) * coefs[i], 0);
  const expected = (10 - (sum % 10)) % 10;
  if (expected !== check) {
    return { valid: false, message: "El dígito verificador de la cédula no coincide. Revisá el número." };
  }
  return { valid: true, message: "" };
}
